/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import RegistrationBenefits from '../../pages/account/registration-benefits/registration-benefits'
import {useGigya} from '../../context/gigya'
import GigyaRegisterController from '../../utils/gigya/registration'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {cssObjectToString, hashSHA256} from '../../utils/utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useWishList} from '../../hooks/use-wish-list'
import {BRAND_KEY} from '../../brand'
import GigyaBasicFormController from '../../utils/gigya/lite-registration'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'

const RegisterForm = ({isThankYouPage = false, email, variant}) => {
    const {gigyaObject, gigyaLocale} = useGigya()
    const stylesLogin = useMultiStyleConfig('Login')
    const stylesModal = useMultiStyleConfig('GigyaModal', {variant: 'native'})
    const {addLayer} = useGoogleTagManager()
    const navigate = useNavigation()
    const intl = useIntl()
    const ssrGigyaMethods = useGigyaSSR()
    const {getAccountInfo, getLiteToken} = ssrGigyaMethods

    const {data: basket} = useCurrentBasket()
    const {data: wishListData} = useWishList()
    useEffect(() => {
        window.DLG = {
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [])
    useEffect(() => {
        window.DLG = {
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.REDIRECT.LOGIN') {
                        gigyaObject?.accounts?.hideScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                        })
                        navigate('/login')
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [])

    useEffect(() => {
        if (!gigyaObject) return

        gigyaObject.accounts.showScreenSet({
            screenSet: 'Default-RegistrationLogin',
            startScreen: 'gigya-register-screen',
            containerID: 'register',
            lang: gigyaLocale,
            deviceType: 'desktop',
            onSubmit: (e) => {
                GigyaRegisterController.onSubmit(e, null, isThankYouPage ? 'ECOMMERCE_GUEST' : null)
            },
            onBeforeScreenLoad: (event) => {
                if (!event.context || event.context != 'ownid-reg-completion') {
                    //For registration completion screen
                    if (
                        event.nextScreen === 'gigya-complete-registration-screen' &&
                        String(event?.response?.errorCode) === '206001'
                    ) {
                        // Append the modal styles to the head
                        let style = document.createElement('style')
                        style.innerHTML = cssObjectToString(stylesModal)
                        document.getElementsByTagName('head')[0].appendChild(style)
                        gigyaObject.accounts.showScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                            startScreen: 'gigya-complete-registration-screen',
                            lang: gigyaLocale,
                            regToken:
                                event?.response?.response?.regToken || event?.response?.regToken,
                            onBeforeScreenLoad: (e) => {
                                // Add the correct close icon to the modal
                                const closeBtn = document.querySelector(
                                    '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-close a',
                                )
                                const oldImg = closeBtn?.querySelector('img')
                                if (oldImg) {
                                    oldImg.remove()

                                    const svg = document.createElement('span')
                                    svg.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
                                    closeBtn.appendChild(svg)
                                }

                                if (e?.nextScreen === 'gigya-verification-sent-screen') {
                                    const mainContainer = document.querySelector(
                                        '.gigya-screen-dialog-main',
                                    )
                                    if (mainContainer) {
                                        mainContainer.classList.add('dlg-screen-dialog-main')
                                    }
                                }
                            },
                            onAfterScreenLoad: (e) => {
                                const emailInput = document.querySelector(
                                    'input[name="profile.email"]',
                                )

                                if (emailInput) {
                                    emailInput.classList.add('gigya-valid')
                                    emailInput.setAttribute('disabled', 'true')
                                }

                                const fistNameInput = document.querySelector(
                                    'input[name="profile.firstName"]',
                                )

                                if (fistNameInput && e?.profile?.firstName) {
                                    fistNameInput.classList.add('gigya-valid')
                                }
                            },
                            onBeforeSubmit: (res) => GigyaRegisterController.onBeforeSubmit(res),
                            onSubmit: (e) => {
                                GigyaBasicFormController.onSubmit(
                                    e,
                                    null,
                                    '.gigya-input-checkbox:not(.subscription-checkbox)',
                                )
                            },
                        })

                        return false
                    }

                    if (!event.context) {
                        if (
                            event.nextScreen === 'gigya-verification-pending-screen' &&
                            String(event?.response?.errorCode) === '206002'
                        ) {
                            let style = document.createElement('style')
                            style.innerHTML = cssObjectToString(stylesModal)
                            document.getElementsByTagName('head')[0].appendChild(style)
                            gigyaObject.accounts.resendVerificationCode({
                                regToken:
                                    event?.response?.response?.regToken ||
                                    event?.response?.regToken,
                            })
                            gigyaObject.accounts.showScreenSet({
                                screenSet: 'Default-RegistrationLogin',
                                startScreen: 'gigya-verification-pending-screen',
                                regToken:
                                    event?.response?.response?.regToken ||
                                    event?.response?.regToken,
                                lang: gigyaLocale,
                                onBeforeScreenLoad: () => {
                                    let style = document.createElement('style')
                                    style.innerHTML = cssObjectToString(stylesModal)
                                    document.getElementsByTagName('head')[0].appendChild(style)
                                    const closeBtn = document.querySelector(
                                        '.gigya-screen-dialog-top .gigya-screen-dialog-close a',
                                    )

                                    const oldImg = closeBtn?.querySelector('img')
                                    if (oldImg) {
                                        oldImg.remove()

                                        const svg = document.createElement('span')
                                        svg.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
                                        closeBtn.appendChild(svg)
                                    }

                                    const modalTitle = document.querySelector(
                                        '.gigya-screen-dialog-top .gigya-screen-dialog-caption',
                                    )

                                    if (modalTitle) {
                                        modalTitle.style.display = 'none'

                                        const customTitle = document.createElement('div')
                                        customTitle.className = modalTitle.className
                                        customTitle.id = 'custom-modal-title'

                                        customTitle.textContent = intl.formatMessage({
                                            id: 'screensets.registration_pending.title',
                                            defaultMessage: 'Your account is almost ready',
                                        })

                                        modalTitle.parentNode.insertBefore(customTitle, modalTitle)
                                    }
                                },
                            })
                            return false
                        }
                    }
                }
            },
            onAfterScreenLoad: async (e) => {
                GigyaRegisterController.onAfterScreenLoad(e, isThankYouPage, email)
                const response = await getLiteToken(email)
                const data = await getAccountInfo(null, response?.token)
                if (isThankYouPage) {
                    // Prefill marketing consent checkbox
                    const lang = window.gigya.thisScript.lang.originalLang
                    const countryCode = window.gigya.thisScript.globalConf.countryCodeMapping[lang]
                    const hasConsent = Object.values(data?.preferences || {})?.some(
                        (pref) =>
                            pref?.entitlements.includes(`${BRAND_KEY}${countryCode}`) &&
                            pref?.isConsentGranted === true,
                    )

                    if (hasConsent) {
                        const checkbox = document.querySelector(
                            '#register form div.gigya-layout-row div.gigya-custom-checkbox input.gigya-input-checkbox',
                        )

                        setTimeout(() => {
                            checkbox.click()
                        }, 100)
                    }
                }
            },
            onBeforeValidation: () => {},
            onError: () => {},
            onFieldChanged: () => {},
            onBeforeSubmit: (res) => GigyaRegisterController.onBeforeSubmit(res),
            onAfterSubmit: async (res) => {
                GigyaRegisterController.onAfterSubmit(res, ssrGigyaMethods)
                await addLayer(
                    basic_gtm_event.bind(this, {
                        event: 'reservedArea',
                        eventCategory: 'loyalty',
                        eventAction: 'reservedArea',
                        eventLabel: 'predoubleoptin-signUp',
                        userId: res && res.response && res.response.UID,
                        email: res && res.profile && res.profile.email,
                        emailSHA256: await hashSHA256(res.profile.email),
                    }),
                )
                localStorage.setItem('guestBasket', JSON.stringify(basket))
                localStorage.setItem('guestWishList', JSON.stringify(wishListData))
            },
        })
    }, [gigyaObject])

    return (
        <>
            <Stack
                {...(isThankYouPage
                    ? stylesLogin.registrationFormStackThankYou
                    : variant === 'preCheckout'
                    ? stylesModal.regustrationFormStackSubscriptions
                    : stylesLogin.registrationFormStack)}
                __css={{
                    ...stylesLogin,
                    '.dln-registrationForm__paragraph--secondary': {
                        display: 'none',
                    },
                    '.gigya-composite-control-social-login': {
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            display: 'block',
                            width: '100%',
                            height: '1px',
                            backgroundColor: '#D4D4D4',
                            position: 'absolute',
                            bottom: '-16px',
                        },
                    },
                    '.gigya-composite-control.gigya-composite-control-social-login': {
                        marginBottom: '32px',
                    },
                }}
            >
                {isThankYouPage && (
                    <Text {...stylesLogin.thankYouRegistrationTitle}>
                        <FormattedMessage
                            defaultMessage="Email"
                            id="login_form.message.registration_title_thank_you_guest"
                        />
                    </Text>
                )}
                <div className="login-content" id="register"></div>
            </Stack>
            {variant !== 'preCheckout' && (
                <Stack {...stylesLogin.registrationStack}>
                    {isThankYouPage ? (
                        <Text {...stylesLogin.thankYouRegistrationTitle}>
                            <FormattedMessage
                                defaultMessage="Create an account and register your product"
                                id="login_form.message.benefits_title_thank_you_guest"
                            />
                        </Text>
                    ) : (
                        <>
                            <Text {...stylesLogin.registrationTitle}>
                                <FormattedMessage
                                    defaultMessage="Your benefits"
                                    id="login_form.message.registration_title"
                                />
                            </Text>
                            <Text {...stylesLogin.registrationSutitle}>
                                <FormattedMessage
                                    defaultMessage="Join the De’Longhi family to take advantage of exclusive deals and a more customised experience."
                                    id="login_form.message.registration_subtitle"
                                />
                            </Text>
                        </>
                    )}

                    <RegistrationBenefits isThankYouPage={isThankYouPage} />
                </Stack>
            )}
        </>
    )
}

RegisterForm.propTypes = {
    isThankYouPage: PropTypes.bool,
    email: PropTypes.string,
    variant: PropTypes.string,
}

export default RegisterForm
